import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { 
  Target, Plus, Trash2, Edit2, Sparkles, Trophy,
  Loader2, PartyPopper
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Common goal icons
const GOAL_ICONS = ['🎯', '🎮', '🚲', '📱', '🎸', '⚽', '🎨', '📚', '🎁', '💰', '🏆', '✨'];

/**
 * Single Goal Card with Progress
 */
export const GoalCard = ({ goal, onEdit, onDelete, className }) => {
  const isCompleted = goal.is_completed;
  
  return (
    <div 
      className={cn(
        "relative p-3 sm:p-4 rounded-2xl border-2 transition-all",
        isCompleted 
          ? "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-300" 
          : "bg-white border-slate-200 hover:border-purple-300",
        className
      )}
      data-testid={`goal-card-${goal.id}`}
    >
      {/* Completion celebration */}
      {isCompleted && (
        <div className="absolute -top-2 -right-2">
          <div className="bg-amber-400 text-white p-1.5 rounded-full animate-bounce">
            <Trophy className="w-4 h-4" />
          </div>
        </div>
      )}
      
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <span className="text-2xl sm:text-3xl shrink-0">{goal.icon}</span>
          <div className="min-w-0">
            <h4 className="font-semibold text-slate-800 truncate text-sm sm:text-base">{goal.name}</h4>
            <p className="text-xs sm:text-sm text-slate-500">
              ${goal.current_amount.toFixed(2)} / ${goal.target_amount.toFixed(2)}
            </p>
          </div>
        </div>
        
        {!isCompleted && (
          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-slate-400 hover:text-slate-600"
              onClick={() => onEdit(goal)}
              data-testid={`edit-goal-${goal.id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-slate-400 hover:text-red-500"
              onClick={() => onDelete(goal.id)}
              data-testid={`delete-goal-${goal.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      
      {/* Progress bar */}
      <div className="space-y-2">
        <Progress 
          value={goal.progress_percent} 
          className={cn(
            "h-3",
            isCompleted ? "[&>div]:bg-amber-400" : "[&>div]:bg-gradient-to-r [&>div]:from-purple-500 [&>div]:to-pink-500"
          )}
        />
        <div className="flex justify-between text-xs">
          <span className={isCompleted ? "text-amber-600 font-medium" : "text-slate-500"}>
            {isCompleted ? "🎉 Goal reached!" : `${goal.progress_percent.toFixed(0)}% saved`}
          </span>
          <span className="text-slate-400">
            ${(goal.target_amount - goal.current_amount).toFixed(2)} to go
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Savings Goals Section Component
 */
export const SavingsGoals = ({ 
  goals = [], 
  loading = false,
  onCreateGoal,
  onUpdateGoal,
  onDeleteGoal 
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({ name: '', target_amount: '', icon: '🎯' });
  const [submitting, setSubmitting] = useState(false);

  const completedGoals = goals.filter(g => g.is_completed);
  const activeGoals = goals.filter(g => !g.is_completed);

  const handleOpenCreate = () => {
    setEditingGoal(null);
    setFormData({ name: '', target_amount: '', icon: '🎯' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({ 
      name: goal.name, 
      target_amount: goal.target_amount.toString(), 
      icon: goal.icon 
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const goalData = {
        name: formData.name,
        target_amount: parseFloat(formData.target_amount),
        icon: formData.icon
      };

      if (editingGoal) {
        await onUpdateGoal(editingGoal.id, goalData);
      } else {
        await onCreateGoal(goalData);
      }
      
      setDialogOpen(false);
      setFormData({ name: '', target_amount: '', icon: '🎯' });
      setEditingGoal(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (goalId) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      await onDeleteGoal(goalId);
    }
  };

  return (
    <Card className="shadow-lg rounded-2xl" data-testid="savings-goals-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-500" />
              My Savings Goals
            </CardTitle>
            <CardDescription>Set goals and track your progress!</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                onClick={handleOpenCreate}
                data-testid="add-goal-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Goal
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="goal-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  {editingGoal ? 'Edit Goal' : 'New Savings Goal'}
                </DialogTitle>
                <DialogDescription>
                  {editingGoal ? 'Update your goal details' : 'What are you saving for?'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Icon Selection */}
                <div className="space-y-2">
                  <Label>Pick an icon</Label>
                  <div className="flex flex-wrap gap-2">
                    {GOAL_ICONS.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setFormData({ ...formData, icon })}
                        className={cn(
                          "w-10 h-10 text-xl rounded-lg border-2 transition-all hover:scale-110",
                          formData.icon === icon 
                            ? "border-purple-500 bg-purple-50" 
                            : "border-slate-200 hover:border-slate-300"
                        )}
                        data-testid={`icon-${icon}`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="goal-name">Goal Name</Label>
                  <Input
                    id="goal-name"
                    data-testid="goal-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., New bike, Video game..."
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="goal-amount">Target Amount ($)</Label>
                  <Input
                    id="goal-amount"
                    data-testid="goal-amount-input"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.target_amount}
                    onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                    placeholder="50.00"
                    required
                    className="text-lg"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  disabled={submitting}
                  data-testid="submit-goal"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {editingGoal ? 'Update Goal' : 'Create Goal'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : goals.length === 0 ? (
          <div className="text-center py-8">
            <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No savings goals yet</p>
            <p className="text-slate-400 text-sm">Create your first goal above!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active Goals */}
            {activeGoals.length > 0 && (
              <div className="space-y-3">
                {activeGoals.map((goal) => (
                  <GoalCard 
                    key={goal.id} 
                    goal={goal} 
                    onEdit={handleOpenEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}

            {/* Completed Goals */}
            {completedGoals.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-amber-600 font-medium">
                  <PartyPopper className="w-4 h-4" />
                  Completed Goals ({completedGoals.length})
                </div>
                {completedGoals.map((goal) => (
                  <GoalCard 
                    key={goal.id} 
                    goal={goal} 
                    onEdit={handleOpenEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SavingsGoals;
